import { useEffect, useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { LoaderCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { workspaceUrl } from "../workspace/WorkspacePicker";
import { fetchWorkspaceSession } from "../workspace/session";
import { authKitLoginUrl } from "./lib/auth-kit";
import { MarketingLayout } from "./sections/Layout";

// Sign-in is WorkOS AuthKit. This page either:
//   - confirms a just-set session (?welcome=1) and routes into a workspace
//   - shows a failure state (?auth=failed)
//   - otherwise bounces to GET /auth/login, which redirects to AuthKit

type LoginState =
  | { kind: "redirecting" }
  | { kind: "verifying" }
  | { kind: "error"; message: string };

export function Login(): ReactElement {
  const search = useSearch({ from: "/login" });
  const isWelcome = Number(search.welcome) === 1;
  const authFailed = search.auth === "failed";
  const welcomeSlug = search.slug;

  const [state, setState] = useState<LoginState>(
    isWelcome
      ? { kind: "verifying" }
      : authFailed
        ? {
            kind: "error",
            message: "Sign-in didn't complete. Try again.",
          }
        : { kind: "redirecting" },
  );
  const welcomeQuery = useQuery({
    queryKey: ["auth", "welcome", welcomeSlug],
    enabled: isWelcome,
    queryFn: fetchWorkspaceSession,
    retry: false,
  });

  useEffect(() => {
    if (isWelcome || authFailed) return;
    window.location.replace(authKitLoginUrl(welcomeSlug));
  }, [authFailed, isWelcome, welcomeSlug]);

  useEffect(() => {
    if (!isWelcome) return;
    if (welcomeQuery.isPending) {
      setState({ kind: "verifying" });
      return;
    }
    if (welcomeQuery.isError) {
      setState({
        kind: "error",
        message: welcomeQuery.error.message,
      });
      return;
    }
    const session = welcomeQuery.data;
    if (!session) return;

    const named = welcomeSlug
      ? session.workspaces.find((workspace) => workspace.id === welcomeSlug)
      : undefined;
    if (named) {
      window.location.replace(workspaceUrl(named));
      return;
    }
    if (session.workspaces.length === 1) {
      window.location.replace(workspaceUrl(session.workspaces[0]));
      return;
    }

    window.location.replace("/workspaces");
  }, [
    isWelcome,
    welcomeQuery.data,
    welcomeQuery.error,
    welcomeQuery.isError,
    welcomeQuery.isPending,
    welcomeSlug,
  ]);

  return (
    <MarketingLayout>
      <section className="mx-auto w-full max-w-md px-6 py-16 sm:py-24">
        {state.kind === "redirecting" || state.kind === "verifying" ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 p-8 text-center text-sm text-muted-foreground">
              <LoaderCircle
                className="size-5 animate-spin"
                aria-hidden="true"
              />
              {state.kind === "verifying"
                ? "Confirming your session…"
                : "Redirecting to sign-in…"}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-semibold tracking-tight">
                Couldn't sign you in.
              </h2>
              <p className="mt-3 text-sm text-muted-foreground">{state.message}</p>
              <div className="mt-8 flex items-center justify-center gap-3">
                <Button asChild>
                  <a href={authKitLoginUrl(welcomeSlug)}>Try again</a>
                </Button>
                <Button asChild variant="ghost">
                  <Link to="/">Back to home</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </section>
    </MarketingLayout>
  );
}
